class ApiCache {
  private cache = new Map<string, { data: any; timestamp: number; loading: boolean }>();
  private readonly CACHE_DURATION = 2 * 60 * 1000; // 2 minutes

  async get<T>(key: string, fetcher: () => Promise<T>): Promise<T> {
    const cached = this.cache.get(key);
    const now = Date.now();

    // Return cached data if still fresh
    if (cached && (now - cached.timestamp < this.CACHE_DURATION)) {
      return cached.data;
    }

    // Prevent duplicate requests
    if (cached?.loading) {
      // Wait for ongoing request
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          const current = this.cache.get(key);
          if (current && !current.loading) {
            clearInterval(checkInterval);
            resolve(current.data);
          }
        }, 100);
      });
    }

    // Set loading state
    this.cache.set(key, { data: cached?.data, timestamp: cached?.timestamp || 0, loading: true });

    try {
      const data = await fetcher();
      this.cache.set(key, { data, timestamp: now, loading: false });
      return data;
    } catch (error) {
      // Remove loading state on error
      if (cached) {
        this.cache.set(key, { ...cached, loading: false });
      } else {
        this.cache.delete(key);
      }
      throw error;
    }
  }

  clear(key?: string) {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  getLastUpdated(key: string): number | null {
    const cached = this.cache.get(key);
    return cached ? cached.timestamp : null;
  }

  getStats() {
    const entries = Array.from(this.cache.entries());
    return {
      totalEntries: entries.length,
      loadingEntries: entries.filter(([, v]) => v.loading).length,
      freshEntries: entries.filter(([, v]) => Date.now() - v.timestamp < this.CACHE_DURATION).length
    };
  }
}

export const apiCache = new ApiCache();